import { useState, useEffect, useRef, useCallback } from "react";
import Carousel from "react-multi-carousel";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { setScrollHidden, safeUrl, checkNativePage } from "@/libs/utils";
import ShowImageModal from "@/components/listing/ShowImageModal";
import ShareListingModal from "@/components/listing/ShareListingModal";
import GripHorizontalIcon from "@/components/Icon/GripHorizontalIcon";
import HeartBlackIcon from "@/components/Icon/HeartBlackIcon";
import ShareBlackIcon from "@/components/Icon/ShareBlackIcon";
import { useAuthValue } from "@/contexts/contextAuth";

import "react-multi-carousel/lib/styles.css";
import { makeApiImage, publicDomain } from "@/libs/clientConfig";
import { useRouteHistoryStore } from "@/stores/route-history.store";
import { useRouter } from "next/router";
import NextImageWithLoader from "../NextImageWithLoader";

const defaultImage = "/assets/imagePlaceholder.svg";

type Props = {
  data: any;
  slug: string;
};

const ImageSection = ({ data, slug }: Props) => {
  try {
    const ref = useRef(null);
    const carousel_length =
      data?.uploadImages == null ? 0 : data?.uploadImages.length;

    const router = useRouter();
    const { lastRegisteredRoute } = useRouteHistoryStore();
    const [imageURI, setImageURI] = useState<string>(
      Array.isArray(data?.uploadImages) && data.uploadImages.length > 0
        ? data.uploadImages.find((i: any) => i?.primaryStatus == true)
            ?.images ?? data.uploadImages[0]?.images
        : ""
    );
    const [showImageModalIsOpened, setShowImageModalIsOpened] =
      useState<boolean>(false);
    const [carouselImageRenderedIndex, setCarouselImageRenderedIndex] =
      useState(0);
    const [carouselImageIndex, setCarouselImageIndex] = useState(0);

    const shouldRenderCarouselImage = useCallback(
      (index: number) => {
        const buffer = 2;
        const limit = carouselImageRenderedIndex + buffer;
        return index <= limit;
      },
      [carouselImageRenderedIndex]
    );
    const [shareLisingModalIsOpened, setShareListingModalIsOpened] =
      useState<boolean>(false);
    const [scrollPosition, setScrollPosition] = useState<number>(0);

    const { setIsSignup, isShow, setIsFavMark } = useAuthValue();

    const responsive = {
      mobile: {
        breakpoint: { max: 925, min: 0 },
        items: 1,
      },
    };

    const isNative = checkNativePage();

    //To get scroll width
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);
    };

    const changeImage = (image_uri: string) => {
      setImageURI(image_uri);
    };

    const markVehicleFavorite = () => {
      const isFavMark = `${slug}?action=fav&favId=${data._id}`;
      window?.dataLayer &&
        window.dataLayer.push({
          event: "Favorite",
          Login: "pre",
        });
      setIsFavMark(isFavMark);
      setIsSignup(true, { isFavMark });
      setScrollHidden();
    };

    useEffect(() => {
      setImageURI(
        Array.isArray(data?.uploadImages) && data.uploadImages.length > 0
          ? data.uploadImages.find((i: any) => i?.primaryStatus == true)?.images
              ?.images ?? data.uploadImages[0]?.images
          : "" || ""
      );
    }, [data]);

    const goBack = () => {
      const previousRoute = lastRegisteredRoute[0];
      if (
        previousRoute?.path.includes("buy") ||
        previousRoute?.path.includes("native")
      ) {
        router.back();
      } else {
        router.push(isNative ? "/native/buy" : "/buy");
      }
    };

    useEffect(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);
    return (
      <section>
        <div
          className={`${
            scrollPosition > 56 && "fixed top-[56px]"
          } w-full bg-white flex sm:hidden justify-between items-center py-6 sm:pt-2 z-50`}
        >
          <div
            className="flex items-center hover:underline cursor-pointer px-4 md:px-0"
            onClick={() => goBack()}
          >
            <span>
              <Image
                width={14}
                height={14}
                src="/assets/listings/arrow.png"
                alt="back"
              />
            </span>
            <span className="text-base text-[#212529] font-medium leading-5">
              &nbsp;&nbsp;Back to search
            </span>
          </div>

          <div className="flex items-center space-x-4 px-4">
            <HeartBlackIcon
              className="cursor-pointer filter drop-shadow-md"
              onClick={() => markVehicleFavorite()}
            />
            <ShareBlackIcon
              className="cursor-pointer filter drop-shadow-md"
              onClick={() => {
                setShareListingModalIsOpened(true);
                setScrollHidden();
              }}
            />
          </div>
        </div>
        <div
          className={`${
            scrollPosition > 122
              ? "fixed top-[0px] w-full md:max-w-[1000px] py-6"
              : "pt-8"
          } w-full bg-white hidden sm:flex justify-between items-center z-40`}
        >
          <div
            className="flex items-center hover:underline cursor-pointer px-4 md:px-0"
            onClick={() => goBack()}
          >
            <span>
              <Image
                width={14}
                height={14}
                src="/assets/listings/arrow.png"
                alt="back"
              />
            </span>
            <span className="text-base text-[#212529] font-medium leading-5">
              &nbsp;&nbsp;Back to search
            </span>
          </div>
          <div className="flex items-center space-x-4 px-4">
            <HeartBlackIcon
              className="cursor-pointer filter drop-shadow-md"
              onClick={() => markVehicleFavorite()}
            />
            <ShareBlackIcon
              className="cursor-pointer filter drop-shadow-md"
              onClick={() => {
                setShareListingModalIsOpened(true);
                setScrollHidden();
              }}
            />
          </div>
        </div>

        {data?.originalSourceInfo && (
          <div className="w-full h-[40px] text-[16px] font-[600] bg-[#FFEDDE] my-3 py-3  flex justify-center items-center">
            This vehicle listed on {data?.originalSourceInfo?.sourceSite}
          </div>
        )}
        <div
          className={`w-full ${
            scrollPosition > 56 ? "mt-[150px] sm:mt-0" : "mt-0"
          }`}
        >
          {data ? (
            <div className="relative">
              <div className="w-full hidden sm:flex justify-center sm:justify-between">
                <div className="md:w-[420px] md:h-[490px] hidden sm:grid grid-cols-2">
                  {Array.from({ length: 4 }).map((_, index) => {
                    const item = data?.uploadImages?.[index];
                    try {
                      const imgUrl = makeApiImage(item?.images);
                      const uploadedImage = (
                        <div
                          className="md:w-[202px] md:h-[240px] mt-2.5 mr-2"
                          key={index}
                        >
                          <Image
                            className="w-[202px] h-[236px] cursor-pointer object-cover"
                            src={imgUrl}
                            width={202}
                            height={236}
                            key={index}
                            onClick={() => item && changeImage(item.images)}
                            alt="car_image_202x236"
                          />
                        </div>
                      );

                      const defaultImageUi = (
                        <div
                          className="h-[240px] w-[202px] border border-gray-200 rounded font-normal text-sm leading-5 text-gray-800 relative z-10 flex flex-col items-center justify-center mt-2.5 mr-2"
                          key={index}
                        >
                          <Image
                            className="cursor-pointer object-cover"
                            src={imgUrl}
                            width={48}
                            height={48}
                            key={index}
                            alt="car_image_202x236"
                          />
                          <div className="font-normal text-sm leading-5 text-center text-gray-500 pt-2">
                            No image added
                          </div>
                        </div>
                      );
                      return item ? uploadedImage : defaultImageUi;
                    } catch (e: any) {
                      console.log(
                        `Bad image URL [${item?.images}] (${e.message})`
                      );
                    }
                  })}
                </div>
                <div className="md:w-[570px] relative pt-2.5 pb-1.5 md:pl-1.5">
                  <div className="flex justify-center">
                    {data.hasPendingDeal && (
                      <div className="absolute top-5 left-4 z-10 l rounded-md bg-[#f2994a] text-center text-white w-16 h-6 uppercase py-2 px-2 font-semibold text-xs leading-4 flex items-center justify-center">
                        Pending
                      </div>
                    )}
                    <Image
                      width={564}
                      height={480}
                      className="w-[564px] h-[480px] cursor-pointer object-cover"
                      src={makeApiImage(imageURI)}
                      alt={`car_image_564x480 - `}
                      onClick={() => {
                        if (data?.originalSourceInfo) {
                          return;
                        }
                        setShowImageModalIsOpened(true);
                        setScrollHidden();
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="block sm:hidden relative">
                <Carousel
                  ref={ref}
                  additionalTransfrom={0}
                  autoPlay={false}
                  swipeable={true}
                  draggable={true}
                  showDots={false}
                  arrows={false}
                  responsive={responsive}
                  ssr={true}
                  infinite={false}
                  keyBoardControl={true}
                  transitionDuration={500}
                  containerClass="carousel-container"
                  removeArrowOnDeviceType={["tablet", "mobile"]}
                  partialVisible={true}
                  dotListClass="custom-dot-list-style"
                  itemClass="max-h-[300px] md:max-h-full"
                  afterChange={(_, { currentSlide }) => {
                    setCarouselImageRenderedIndex(
                      Math.max(currentSlide, carouselImageRenderedIndex)
                    );
                    setCarouselImageIndex(currentSlide);
                  }}
                >
                  {(data?.uploadImages || []).map(
                    (item: any, index: number) => {
                      return (
                        <div
                          className="relative w-full xs:h-[400px]"
                          key={item.images}
                        >
                          {shouldRenderCarouselImage(index) && (
                            <NextImageWithLoader
                              className="w-full h-full cursor-pointer object-cover"
                              src={makeApiImage(item.images)}
                              width={510}
                              height={288}
                              onClick={() => {
                                if (data?.originalSourceInfo) {
                                  return;
                                }
                                setShowImageModalIsOpened(true);
                                setScrollHidden();
                              }}
                              alt="car_image_1250x1000"
                              style={{
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </div>
                      );
                    }
                  )}
                </Carousel>
                <div className="absolute bottom-2 right-8 bg-black bg-opacity-70 text-white px-2 rounded">
                  {carouselImageIndex + 1}&nbsp;/&nbsp;{carousel_length}
                </div>
              </div>
              {data?.status == "sold" && (
                <div className="absolute top-[45%] left-0 w-full h-[40px] xs:h-[10%] bg-[#1C2332] opacity-90 text-white z-30 flex sm:block justify-center items-center">
                  <div className="sm:absolute top-[10%] left-[67%] w-[120px] text-center">
                    <p className="font-semibold">SOLD</p>
                    <p className="text-xs">Vehicle has been sold.</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            ""
          )}
        </div>
        <AnimatePresence>
          {showImageModalIsOpened && (
            <motion.div
              initial={{ y: -100 }}
              animate={{ y: 0 }}
              exit={{ y: 0, transition: { duration: 0 } }}
              transition={{ duration: 0.3 }}
              className="fixed top-0 left-0 w-full h-screen z-60 inset-0"
            >
              <ShowImageModal
                showImageModalIsOpened={showImageModalIsOpened}
                setShowImageModalIsOpened={setShowImageModalIsOpened}
                data={data}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {showImageModalIsOpened && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black opacity-50"></div>
          </div>
        )}
        {shareLisingModalIsOpened && (
          <ShareListingModal
            shareLisingModalIsOpened={shareLisingModalIsOpened}
            setShareListingModalIsOpened={setShareListingModalIsOpened}
            url={`${publicDomain()}/listing/${data.slug}`}
          />
        )}
      </section>
    );
  } catch (e: any) {
    console.log(e);
    return <></>;
  }
};

export default ImageSection;
